import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  Users,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  UserCheck,
  UserPlus,
  Search,
  BarChart3,
  ArrowRight,
  Clock,
  Mail,
  Phone,
  TrendingUp,
} from 'lucide-react';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';
import { leadAllocationAPI } from '../lib/advanced-features-api';

interface Lead {
  id: string;
  propertyId: string;
  propertyType: string;
  leadInfo: {
    name: string;
    email: string;
    phone: string;
    message?: string;
  };
  timestamp: string;
  status: string;
  assignedTo?: string;
  assignedAt?: string;
}

interface Agent {
  id: string;
  name: string;
  email: string;
}

interface AdminLeadAllocationProps {
  agents: Agent[];
}

export function AdminLeadAllocation({ agents }: AdminLeadAllocationProps) {
  const [activeTab, setActiveTab] = useState('allocation');
  const [unallocatedLeads, setUnallocatedLeads] = useState<Lead[]>([]);
  const [allocatedLeads, setAllocatedLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAllocating, setIsAllocating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showManualAssign, setShowManualAssign] = useState(false);
  const [selectedLeadForAssign, setSelectedLeadForAssign] = useState<Lead | null>(null);
  const [manualAssignAgent, setManualAssignAgent] = useState('');
  const [isManualAssigning, setIsManualAssigning] = useState(false);
  const [agentLeadCounts, setAgentLeadCounts] = useState<Record<string, number>>({});
  const [isLoadingAgentLeads, setIsLoadingAgentLeads] = useState(false);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');

  useEffect(() => {
    loadAllLeadData();
  }, []);

  const loadAllLeadData = async () => {
    setIsLoading(true);
    setIsLoadingAgentLeads(true);
    try {
      const allLeads = await leadAllocationAPI.getAllLeadsAdmin();
      const unallocated: Lead[] = [];
      const allocated: Lead[] = [];
      const counts: Record<string, number> = {};

      allLeads.forEach((lead) => {
        if (lead.assignedTo) {
          allocated.push(lead);
          counts[lead.assignedTo] = (counts[lead.assignedTo] || 0) + 1;
        } else {
          unallocated.push(lead);
        }
      });

      setUnallocatedLeads(unallocated);
      setAllocatedLeads(allocated);
      setAgentLeadCounts(counts);
    } catch (error) {
      logger.error('Error loading lead data', error as Error, undefined, 'AdminLeadAllocation');
      toast.error('Failed to load leads');
    } finally {
      setIsLoading(false);
      setIsLoadingAgentLeads(false);
    }
  };

  const handleAllocateLeads = async () => {
    if (unallocatedLeads.length === 0) {
      toast.info('No leads to allocate');
      return;
    }

    if (activeAgents.length === 0) {
      toast.error('No agents available for allocation');
      return;
    }

    setIsAllocating(true);
    try {
      const leadIds = unallocatedLeads.map(lead => lead.id);
      const agentIds = activeAgents.map(agent => agent.id);

      const result = await leadAllocationAPI.allocateLeads(leadIds, agentIds);

      toast.success(`Successfully allocated ${result.totalAllocated} leads to ${agentIds.length} agents`);

      // Reload all data
      await loadAllLeadData();
    } catch (error) {
      logger.error('Error allocating leads', error as Error, undefined, 'AdminLeadAllocation');
      toast.error('Failed to allocate leads');
    } finally {
      setIsAllocating(false);
    }
  };

  const handleManualAssign = async () => {
    if (!selectedLeadForAssign || !manualAssignAgent) {
      toast.error('Please select a lead and an agent');
      return;
    }

    setIsManualAssigning(true);
    try {
      await leadAllocationAPI.allocateLeads(
        [selectedLeadForAssign.id],
        [manualAssignAgent]
      );

      const agentName = activeAgents.find(a => a.id === manualAssignAgent)?.name || 'Agent';
      toast.success(`Lead assigned to ${agentName}`);

      setShowManualAssign(false);
      setSelectedLeadForAssign(null);
      setManualAssignAgent('');
      await loadAllLeadData();
    } catch (error) {
      logger.error('Error manually assigning lead', error as Error, undefined, 'AdminLeadAllocation');
      toast.error('Failed to assign lead');
    } finally {
      setIsManualAssigning(false);
    }
  };

  const activeAgents = useMemo(
    () => agents.filter(agent => agent.id && agent.id !== 'system'),
    [agents]
  );

  const totalAllocatedLeads = useMemo(
    () => Object.values(agentLeadCounts).reduce((sum, c) => sum + c, 0),
    [agentLeadCounts]
  );

  const agentsWithWorkload = useMemo(
    () =>
      activeAgents
        .map(agent => ({
          ...agent,
          leadCount: agentLeadCounts[agent.id] || 0,
        }))
        .sort((a, b) => b.leadCount - a.leadCount),
    [activeAgents, agentLeadCounts]
  );

  const maxLeads = useMemo(
    () => Math.max(1, ...agentsWithWorkload.map(a => a.leadCount)),
    [agentsWithWorkload]
  );

  const filteredAgents = useMemo(() => {
    if (!agentSearchTerm) return agentsWithWorkload;
    const q = agentSearchTerm.toLowerCase();
    return agentsWithWorkload.filter(
      a => (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q)
    );
  }, [agentsWithWorkload, agentSearchTerm]);

  const filteredActiveAgents = useMemo(() => {
    if (!agentSearchTerm) return activeAgents;
    const q = agentSearchTerm.toLowerCase();
    return activeAgents.filter(
      a => (a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q)
    );
  }, [activeAgents, agentSearchTerm]);

  const filteredUnallocated = useMemo(() => {
    if (!searchQuery) return unallocatedLeads;
    const q = searchQuery.toLowerCase();
    return unallocatedLeads.filter(
      l =>
        (l.leadInfo.name || '').toLowerCase().includes(q) ||
        (l.leadInfo.email || '').toLowerCase().includes(q) ||
        (l.leadInfo.phone || '').includes(q)
    );
  }, [unallocatedLeads, searchQuery]);

  const filteredAllocated = useMemo(() => {
    if (!searchQuery) return allocatedLeads;
    const q = searchQuery.toLowerCase();
    return allocatedLeads.filter(
      l =>
        (l.leadInfo.name || '').toLowerCase().includes(q) ||
        (l.leadInfo.email || '').toLowerCase().includes(q) ||
        (l.assignedTo || '').toLowerCase().includes(q)
    );
  }, [allocatedLeads, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#FDB022] mx-auto" />
          <p className="text-muted-foreground text-sm">Loading lead allocation data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-[#FDB022]" />
            Lead Allocation Management
          </h3>
          <p className="text-muted-foreground mt-1">
            Distribute and manage leads across your agent team
          </p>
        </div>
        <Button
          data-testid="admin-lead-refresh-button"
          onClick={loadAllLeadData}
          variant="outline"
          size="sm"
          disabled={isLoading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <Card className="border-[#FDB022]/20 bg-gradient-to-br from-[#FDB022]/5 to-transparent h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Unallocated</CardTitle>
              <AlertCircle className="h-4 w-4 text-orange-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#FDB022]">{unallocatedLeads.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Awaiting assignment</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Allocated</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-400">{totalAllocatedLeads}</div>
              <p className="text-xs text-muted-foreground mt-1">Assigned to agents</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Agents</CardTitle>
              <Users className="h-4 w-4 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-400">{activeAgents.length}</div>
              <p className="text-xs text-muted-foreground mt-1">In rotation pool</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg per Agent</CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-400">
                {activeAgents.length > 0
                  ? (totalAllocatedLeads / activeAgents.length).toFixed(1)
                  : '0'}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Leads per agent</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Allocation Action */}
      <Card className="border-[#FDB022]/10">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="font-medium mb-2">Auto-Allocate Unassigned Leads</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Distribute {unallocatedLeads.length} unallocated lead{unallocatedLeads.length !== 1 ? 's' : ''}
                {' '}to {activeAgents.length} active agent{activeAgents.length !== 1 ? 's' : ''} using round-robin distribution.
              </p>
              {activeAgents.length === 0 && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No active agents available. Please ensure agents are registered in the system.
                  </AlertDescription>
                </Alert>
              )}
              {unallocatedLeads.length === 0 && (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    All leads have been allocated. Great work!
                  </AlertDescription>
                </Alert>
              )}
            </div>
            <Button
              data-testid="admin-lead-allocate-button"
              onClick={handleAllocateLeads}
              disabled={isAllocating || unallocatedLeads.length === 0 || activeAgents.length === 0}
              size="lg"
              className="bg-[#FDB022] text-black hover:bg-[#FDB022]/90"
            >
              {isAllocating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Allocating...
                </>
              ) : (
                <>
                  <UserCheck className="w-4 h-4 mr-2" />
                  Allocate {unallocatedLeads.length} Lead{unallocatedLeads.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Separator className="opacity-20" />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="allocation">
            <AlertCircle className="w-4 h-4 mr-2" />
            Unallocated ({unallocatedLeads.length})
          </TabsTrigger>
          <TabsTrigger value="history">
            <Clock className="w-4 h-4 mr-2" />
            Allocated ({totalAllocatedLeads})
          </TabsTrigger>
          <TabsTrigger value="workload">
            <BarChart3 className="w-4 h-4 mr-2" />
            Agent Workload
          </TabsTrigger>
        </TabsList>

        {/* Unallocated Leads Tab */}
        <TabsContent value="allocation" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {filteredUnallocated.length > 0 ? (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUnallocated.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="text-xs bg-[#FDB022]/10 text-[#FDB022]">
                              {lead.leadInfo.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{lead.leadInfo.name}</p>
                            {lead.leadInfo.message && (
                              <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                                "{lead.leadInfo.message}"
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-0.5">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Mail className="w-3 h-3" />
                            {lead.leadInfo.email}
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-3 h-3" />
                            {lead.leadInfo.phone}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {lead.propertyType === 'seller' ? 'Seller Property' : 'Regular'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(lead.timestamp).toLocaleDateString('en-ZA')}
                      </TableCell>
                      <TableCell>
                        <Button
                          data-testid="admin-lead-assign-button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedLeadForAssign(lead);
                            setManualAssignAgent('');
                            setShowManualAssign(true);
                          }}
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          Assign
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">
                    {searchQuery ? 'No matching leads found' : 'All leads allocated'}
                  </p>
                  <p className="text-xs mt-1">
                    {searchQuery
                      ? 'Try adjusting your search query'
                      : 'New leads from property inquiries will appear here'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Allocated Leads History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search allocated leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {filteredAllocated.length > 0 ? (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Assigned Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAllocated.map((lead) => {
                    const assignedAgent = activeAgents.find(a => a.id === lead.assignedTo);
                    return (
                      <TableRow key={lead.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="text-xs bg-green-500/10 text-green-400">
                                {lead.leadInfo.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{lead.leadInfo.name}</p>
                              <p className="text-xs text-muted-foreground">{lead.leadInfo.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {lead.leadInfo.phone}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="w-6 h-6">
                              <AvatarFallback className="text-[10px] bg-blue-500/10 text-blue-400">
                                {(assignedAgent?.name || '?').split(' ').map(n => n[0]).join('').substring(0, 2)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">
                              {assignedAgent?.name || lead.assignedTo || 'Unknown'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {lead.assignedAt
                            ? new Date(lead.assignedAt).toLocaleDateString('en-ZA')
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                            Allocated
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">
                    {searchQuery ? 'No matching allocated leads' : 'No allocated leads yet'}
                  </p>
                  <p className="text-xs mt-1">
                    Allocated leads will appear here after assignment
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Agent Workload Tab */}
        <TabsContent value="workload" className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Visual breakdown of lead distribution across your agent team
          </p>

          <Input
            placeholder="Search agents by name or email..."
            value={agentSearchTerm}
            onChange={e => setAgentSearchTerm(e.target.value)}
            className="max-w-sm"
          />

          {isLoadingAgentLeads ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredAgents.length > 0 ? (
            <div className="space-y-3">
              {filteredAgents.map((agent, index) => (
                <motion.div
                  key={agent.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="hover:border-[#FDB022]/20 transition-colors">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="text-sm bg-[#FDB022]/10 text-[#FDB022]">
                            {agent.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium truncate">{agent.name}</p>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="outline"
                                className={
                                  agent.leadCount === 0
                                    ? 'border-gray-500/30 text-gray-400'
                                    : agent.leadCount <= 5
                                    ? 'border-green-500/30 text-green-400'
                                    : agent.leadCount <= 10
                                    ? 'border-yellow-500/30 text-yellow-400'
                                    : 'border-red-500/30 text-red-400'
                                }
                              >
                                {agent.leadCount} lead{agent.leadCount !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">{agent.email}</p>
                          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full rounded-full bg-gradient-to-r from-[#FDB022] to-[#FDB022]/60"
                              initial={{ width: 0 }}
                              animate={{
                                width: `${(agent.leadCount / maxLeads) * 100}%`,
                              }}
                              transition={{ duration: 0.6, delay: index * 0.05 }}
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No agents in rotation pool</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* How It Works */}
          <Card className="border-white/5 bg-white/[0.02]">
            <CardHeader>
              <CardTitle className="text-base">How Round-Robin Allocation Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#FDB022]/10 text-[#FDB022] text-xs font-bold flex-shrink-0 mt-0.5">1</div>
                  <p><strong className="text-foreground">Fair Distribution:</strong> Leads are distributed evenly among all active agents.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#FDB022]/10 text-[#FDB022] text-xs font-bold flex-shrink-0 mt-0.5">2</div>
                  <p><strong className="text-foreground">Rotation Memory:</strong> The system remembers and continues from the last assigned agent.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#FDB022]/10 text-[#FDB022] text-xs font-bold flex-shrink-0 mt-0.5">3</div>
                  <p><strong className="text-foreground">Manual Override:</strong> You can manually assign any lead to a specific agent.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#FDB022]/10 text-[#FDB022] text-xs font-bold flex-shrink-0 mt-0.5">4</div>
                  <p><strong className="text-foreground">Agent Visibility:</strong> Agents see their assigned leads in the Agent Hub.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Manual Assignment Dialog */}
      <Dialog open={showManualAssign} onOpenChange={setShowManualAssign}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manually Assign Lead</DialogTitle>
            <DialogDescription>
              Assign this lead to a specific agent instead of using round-robin
            </DialogDescription>
          </DialogHeader>

          {selectedLeadForAssign && (
            <div className="space-y-4">
              <Card className="bg-white/[0.02]">
                <CardContent className="py-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="text-sm bg-[#FDB022]/10 text-[#FDB022]">
                        {selectedLeadForAssign.leadInfo.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{selectedLeadForAssign.leadInfo.name}</p>
                      <p className="text-xs text-muted-foreground">{selectedLeadForAssign.leadInfo.email}</p>
                      <p className="text-xs text-muted-foreground">{selectedLeadForAssign.leadInfo.phone}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center justify-center">
                <ArrowRight className="w-5 h-5 text-[#FDB022]" />
              </div>

              <div className="space-y-2">
                <Label>Assign to Agent</Label>
                <Select value={manualAssignAgent} onValueChange={setManualAssignAgent}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an agent..." />
                  </SelectTrigger>
                  <SelectContent>
                    <div className="px-2 py-1.5">
                      <Input
                        placeholder="Search agents..."
                        value={agentSearchTerm}
                        onChange={e => setAgentSearchTerm(e.target.value)}
                        className="h-8 text-sm"
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                    {filteredActiveAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        <div className="flex items-center gap-2">
                          <span>{agent.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({agentLeadCounts[agent.id] || 0} leads)
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                    {filteredActiveAgents.length === 0 && (
                      <div className="px-2 py-3 text-sm text-muted-foreground text-center">No agents match</div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex items-center justify-end gap-2">
                <Button data-testid="admin-lead-manual-cancel-button" variant="outline" onClick={() => setShowManualAssign(false)}>
                  Cancel
                </Button>
                <Button
                  data-testid="admin-lead-manual-assign-button"
                  onClick={handleManualAssign}
                  disabled={!manualAssignAgent || isManualAssigning}
                  className="bg-[#FDB022] text-black hover:bg-[#FDB022]/90"
                >
                  {isManualAssigning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Assign Lead
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
